import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

export default function Billing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [plan, setPlan] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 8) {
        if (active) setState("pending");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setPlan(data.plan_id);
          setState("success");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div data-testid="billing-status" className="w-full max-w-md bg-card border border-border p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-primary mx-auto mb-5" />
            <h1 className="font-mono font-bold text-xl">Confirming payment...</h1>
            <p className="text-sm text-muted-foreground mt-2">Verifying your subscription with Stripe.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={44} strokeWidth={1.5} className="text-primary text-glow mx-auto mb-5" />
            <h1 className="font-mono font-bold text-2xl text-primary">Payment successful</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Your <span className="text-foreground font-mono uppercase">{plan}</span> plan is now active. Full defense grid unlocked.
            </p>
            <Button
              data-testid="billing-continue"
              onClick={() => navigate("/app")}
              className="mt-6 rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
            >
              Go to dashboard
            </Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock size={44} strokeWidth={1.5} className="text-[#FFD700] mx-auto mb-5" />
            <h1 className="font-mono font-bold text-xl">Payment processing</h1>
            <p className="text-sm text-muted-foreground mt-2">This is taking a moment. Your plan will update shortly.</p>
            <Button onClick={() => navigate("/app")} className="mt-6 rounded-none font-mono bg-secondary border border-border hover:bg-accent">
              Back to dashboard
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={44} strokeWidth={1.5} className="text-destructive mx-auto mb-5" />
            <h1 className="font-mono font-bold text-xl">Payment not completed</h1>
            <p className="text-sm text-muted-foreground mt-2">Something went wrong or the session was cancelled.</p>
            <Button onClick={() => navigate("/app/pricing")} className="mt-6 rounded-none font-mono bg-secondary border border-border hover:bg-accent">
              Back to pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
